import { useState, useEffect } from "react";
import { format } from "date-fns";
import { CalendarClock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ACTIONS_TAKEN, OFFERS_GIVEN } from "@/hooks/useActivities";

const CATEGORIES = [["target", "Marketing"], ["event", "Event"], ["catering", "Catering"]];

export function AddActivityDialog({ open, onOpenChange, onAdd, defaultDate, leads = [], preselectedLead = null }) {
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [time, setTime] = useState("09:00");
  const [leadId, setLeadId] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [category, setCategory] = useState("target");
  const [actions, setActions] = useState([]);
  const [offers, setOffers] = useState([]);
  const [result, setResult] = useState("");
  const [status, setStatus] = useState("Planned");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setDate(format(defaultDate || new Date(), "yyyy-MM-dd"));
      setTime("09:00");
      if (preselectedLead) { setLeadId(preselectedLead.id); setBusinessName(preselectedLead.business_name); }
      else { setLeadId(""); setBusinessName(""); }
      setCategory(preselectedLead?.is_catering ? "catering" : preselectedLead?.is_event ? "event" : "target");
      setActions([]); setOffers([]); setResult(""); setStatus("Planned");
    }
  }, [open, defaultDate, preselectedLead]);

  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const pickLead = (id) => {
    setLeadId(id);
    const l = leads.find((x) => x.id === id);
    if (l) setBusinessName(l.business_name);
  };

  const submit = async () => {
    if (!businessName.trim()) return;
    setSubmitting(true);
    try {
      await onAdd({
        lead_id: leadId || null,
        scheduled_at: new Date(`${date}T${time}:00`).toISOString(),
        business_name: businessName.trim(),
        actions_taken: actions, offers_given: offers,
        result: result.trim() || null, category, status,
      });
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-primary"><CalendarClock className="h-5 w-5" /> Log / Schedule Activity</DialogTitle>
          <DialogDescription>Track outreach for a target. Future dates are scheduled; today or past are logged.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {leads.length > 0 && !preselectedLead && (
            <div className="space-y-2">
              <Label>Link to Target (optional)</Label>
              <Select value={leadId} onValueChange={pickLead}>
                <SelectTrigger data-testid="activity-lead"><SelectValue placeholder="Select a target / contact" /></SelectTrigger>
                <SelectContent>
                  {leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.business_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <Label>Business Name *</Label>
            <Input value={businessName} data-testid="activity-business" onChange={(e) => setBusinessName(e.target.value)} placeholder="Business name" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2"><Label>Date</Label>
              <Input type="date" value={date} data-testid="activity-date" onChange={(e) => setDate(e.target.value)} /></div>
            <div className="space-y-2"><Label>Time</Label>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} /></div>
            <div className="space-y-2"><Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="activity-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Actions Taken</Label>
            <div className="grid grid-cols-2 gap-2">
              {ACTIONS_TAKEN.map((a) => (
                <label key={a} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={actions.includes(a)} onCheckedChange={() => toggle(actions, setActions, a)} /> {a}
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Offers Given</Label>
            <div className="grid grid-cols-2 gap-2">
              {OFFERS_GIVEN.map((o) => (
                <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={offers.includes(o)} onCheckedChange={() => toggle(offers, setOffers, o)} /> {o}
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-2"><Label>Result / Notes</Label>
            <Textarea value={result} onChange={(e) => setResult(e.target.value)} rows={2} placeholder="What happened?" /></div>
          <div className="flex items-center gap-2">
            <Checkbox id="completed" checked={status === "Completed"} onCheckedChange={(c) => setStatus(c ? "Completed" : "Planned")} />
            <Label htmlFor="completed" className="cursor-pointer">Mark as completed (otherwise scheduled)</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="save-activity" disabled={submitting || !businessName.trim()}>
            {submitting ? "Saving..." : "Save Activity"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
